import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { Period, PeriodInput } from '../api';
import { PeriodForm } from '../PeriodForm';

type PeriodLabelProps = {
  period: Period;
  running?: boolean;
  /** Editable sheet: the label opens the period form in place. */
  onUpdate?: (input: PeriodInput) => void;
  onRemove?: () => void;
  pending?: boolean;
  error?: string | null;
};

/** The left column: the period's name and times; on the editable sheet a click edits them for this week. */
export function PeriodLabel({ period, running, onUpdate, onRemove, pending, error }: PeriodLabelProps) {
  const [open, setOpen] = useState(false);
  const text = (
    <>
      <span className="block font-semibold leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
        {running && <span aria-hidden="true">▸ </span>}
        {period.label}
        {running && <span className="sr-only"> (nu)</span>}
      </span>
      <span className="block text-[0.85em] tabular-nums opacity-70">
        {period.startTime}–{period.endTime}
      </span>
    </>
  );
  const common = { role: 'rowheader' as const, 'aria-current': running ? ('time' as const) : undefined, className: 'px-2 py-1' };
  if (!onUpdate) return <div {...common}>{text}</div>;
  return (
    <div {...common}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="min-h-11 w-full text-left" aria-label={`Tijdvak ${period.label} aanpassen`}>
            {text}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80">
          <p className="mb-2 text-sm opacity-70">Alleen deze week; het rooster blijft zoals het is.</p>
          <PeriodForm
            initial={period}
            submitLabel="Opslaan"
            pending={Boolean(pending)}
            onSubmit={(input) => {
              onUpdate(input);
              setOpen(false);
            }}
            onCancel={() => setOpen(false)}
          />
          {onRemove && (
            <Button
              variant="link"
              className="mt-2 px-0 text-[var(--danger)]"
              onClick={() => {
                onRemove();
                setOpen(false);
              }}
            >
              Dit tijdvak uit deze week verwijderen
            </Button>
          )}
          {error && (
            <p role="alert" className="mt-2 text-sm">
              {error}
            </p>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
